import React, { useEffect, useRef, useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import { showToast } from "../../../store/toast/toastSlice";
import { useDispatch } from "react-redux";
import { fetchSubQualificationTypes } from "../../../services/api/qualification";
import { addUserQualifications } from "../../../services/api/user";

const AddBackgroundVerificationModal = ({
  open,
  close,
  refetchUserQualificationsByType,
  backgroundCheckId,
  targetUserId,
}) => {
  const educationFileInputRef = useRef(null);
  const workingRightsFileInputRef = useRef(null);
  const dispatch = useDispatch(null);

  const [selectedEducationFile, setSelectedEducationFile] = useState();
  const [selectedWorkingRightsFile, setSelectedWorkingRightsFile] = useState();

  const {
    isPending: isUserQualificationsApiLoading,
    error: isUserQualificationsApiError,
    data: userQualificationData,
    mutateAsync: addUserQualificationsMutation,
  } = useMutation({
    mutationFn: (payload) => addUserQualifications(payload, targetUserId),
    enabled: false,
  });

  const {
    isLoading: isSubQualificationsLoading,
    error: isSubQualificationsError,
    data: subQualificationsData,
  } = useQuery({
    queryKey: ["subQualifications", backgroundCheckId],
    queryFn: () => fetchSubQualificationTypes(backgroundCheckId),
    enabled: !!backgroundCheckId,
  });

  const handleWorkingRightsFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedWorkingRightsFile(file);
    }
  };
  const handleEducationFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedEducationFile(file);
    }
  };

  const handleRemoveEducationFile = () => {
    setSelectedEducationFile(null);
    if (educationFileInputRef.current) educationFileInputRef.current.value = "";
  };
  const handleRemoveWorkingRightsFile = () => {
    setSelectedWorkingRightsFile(null);
    if (workingRightsFileInputRef.current) workingRightsFileInputRef.current.value = "";
  };

  const saveButtonClicked = async () => {
    try {
      const educationFormData = new FormData();
      const workingRightsFormData = new FormData();

      // Conditionally append the education file if it exists
      if (selectedEducationFile) {
        educationFormData.append("file", selectedEducationFile);
        const educationQualificationTypeId = subQualificationsData?.data.find(
          (q) => q.name.toLowerCase() === "education",
        );
        educationFormData.append("qualificationSubTypeId", educationQualificationTypeId?.id);
      }

      // Conditionally append the working rights file if it exists
      if (selectedWorkingRightsFile) {
        workingRightsFormData.append("file", selectedWorkingRightsFile);
        const workingRightsQualificationTypeId = subQualificationsData?.data.find(
          (q) => q.name.toLowerCase() === "working rights",
        );
        workingRightsFormData.append(
          "qualificationSubTypeId",
          workingRightsQualificationTypeId?.id,
        );
      }

      // Only call the education upload API if the education file is selected
      let educationResponse;
      if (selectedEducationFile) {
        educationResponse = await addUserQualificationsMutation(educationFormData);
      }

      // Only call the working rights upload API if the working rights file is selected
      let workingRightsResponse;
      if (selectedWorkingRightsFile) {
        workingRightsResponse = await addUserQualificationsMutation(workingRightsFormData);
      }

      // Dispatch success toast message
      dispatch(
        showToast({
          message: "Will be verified in two working days.",
          type: "success",
        }),
      );

      refetchUserQualificationsByType();

      // Close the modal and reset the files
      close(false);
      setSelectedEducationFile(null);
      setSelectedWorkingRightsFile(null);
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal className="-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-7 px-5 sm:px-12 w-full lg:w-[900px] xl:w-[1000px]">
        <h6 className="text-dark font-normal border-b pb-1">Background Verifications</h6>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
          <div className="my-3 lg:col-span-1 col-span-2">
            <p className="font-normal text-base mb-2">Education</p>
            <div className="w-full flex flex-wrap items-center gap-3">
              {/* Hidden File Input */}
              <input
                type="file"
                ref={educationFileInputRef}
                onChange={handleEducationFileChange}
                style={{ display: "none" }}
              />

              {/* File Display Section */}
              <div
                style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                className="w-full flex justify-between items-center px-3 py-1 rounded-md"
              >
                <div className="flex-1 min-w-0 py-[6.25px] rounded-md flex flex-wrap items-center">
                  {selectedEducationFile ? (
                    <div className="my-[2px] inline-flex items-center gap-2 bg-[#E9E9EB] rounded-[4px] py-1 pl-4 pr-2 max-w-full">
                      <a
                        href={URL.createObjectURL(selectedEducationFile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer text-blue-500 hover:underline font-300 truncate"
                      >
                        {selectedEducationFile?.name || "View File"}
                      </a>
                      <button
                        type="button"
                        onClick={handleRemoveEducationFile}
                        className="flex items-center justify-center h-4 w-4 rounded-full text-gray-600 hover:bg-gray-300 hover:text-gray-800"
                        title="Remove file"
                      >
                        <i className="fa-solid fa-xmark text-xs"></i>
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-400 py-1 ml-2">No file selected</p>
                  )}
                </div>
                <BaseButton
                  variant="outlined"
                  color="primary"
                  className="py-[7px] px-5 shrink-0 whitespace-nowrap"
                  onClick={() => educationFileInputRef.current.click()}
                >
                  Choose File
                </BaseButton>
              </div>

              {/* Choose File Button */}
            </div>
            <p className="mt-1">Please upload your education certificate (JPG, PNG, PDF)</p>
          </div>
          <div className="my-3 lg:col-span-1 col-span-2">
            <p className="font-normal text-base mb-2">Working Rights</p>
            <div className="w-full flex flex-wrap items-center gap-3">
              {/* Hidden File Input */}
              <input
                type="file"
                ref={workingRightsFileInputRef}
                multiple
                onChange={handleWorkingRightsFileChange}
                style={{ display: "none" }}
              />

              <div
                style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                className="w-full flex justify-between items-center px-3 py-1 rounded-md"
              >
                <div className="flex-1 min-w-0 py-[6.25px] rounded-md flex flex-wrap items-center">
                  {selectedWorkingRightsFile ? (
                    <div className="my-[2px] inline-flex items-center gap-2 bg-[#E9E9EB] rounded-[4px] py-1 pl-4 pr-2 max-w-full">
                      <a
                        href={URL.createObjectURL(selectedWorkingRightsFile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer text-blue-500 hover:underline font-300 truncate"
                      >
                        {selectedWorkingRightsFile?.name || "View File"}
                      </a>
                      <button
                        type="button"
                        onClick={handleRemoveWorkingRightsFile}
                        className="flex items-center justify-center h-4 w-4 rounded-full text-gray-600 hover:bg-gray-300 hover:text-gray-800"
                        title="Remove file"
                      >
                        <i className="fa-solid fa-xmark text-xs"></i>
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-400 py-1 ml-2">No file selected</p>
                  )}
                </div>
                <BaseButton
                  variant="outlined"
                  color="primary"
                  className="py-[7px] px-5 shrink-0 whitespace-nowrap"
                  onClick={() => workingRightsFileInputRef.current.click()}
                >
                  Choose File
                </BaseButton>
              </div>
            </div>
            <p className="mt-1">
              Please upload documents that shows your working rights in your country (JPG, PNG, PDF)
            </p>
          </div>
        </div>
      </div>
      <div
        className="d-flex content-center  bg-white py-6 "
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className=" px-[28px] py-[5px] bg-light text-primary hover:text-primary"
          variant="outlined"
          color="primary"
          style={{
            marginRight: "16px",
          }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isUserQualificationsApiLoading}
          loaderSize={24}
          loaderColor="primary"
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          // Disabled by default; enabled only once a file has been selected.
          disabled={
            (!selectedEducationFile && !selectedWorkingRightsFile) || isUserQualificationsApiLoading
          }
          onClick={saveButtonClicked}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default AddBackgroundVerificationModal;
